import axios from 'axios';
import { getDeviceFingerprint } from '../utils/device';

const getApiBase = () => {
  if (process.env.REACT_APP_API_BASE) return process.env.REACT_APP_API_BASE;
  if (process.env.NODE_ENV === 'production') return '/api';
  return 'http://192.168.103.54:3001/api';
};

const API_BASE = getApiBase();

const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
});

let cachedDeviceFingerprint;

const getSafeDeviceFingerprint = () => {
  if (cachedDeviceFingerprint !== undefined) return cachedDeviceFingerprint;
  try {
    cachedDeviceFingerprint = getDeviceFingerprint() || '';
  } catch {
    cachedDeviceFingerprint = '';
  }
  return cachedDeviceFingerprint;
};

const buildDownloadQuery = (params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.set(key, value);
    }
  });

  const fingerprint = getSafeDeviceFingerprint();
  if (fingerprint) query.set('fp', fingerprint);

  const queryString = query.toString();
  return queryString ? `?${queryString}` : '';
};

const withDownloadTicket = (endpoint, ticket) => {
  const separator = endpoint.includes('?') ? '&' : '?';
  return `${endpoint}${separator}download_ticket=${encodeURIComponent(ticket)}`;
};

export const downloadApiFile = async (endpoint, fallbackName = 'download') => {
  const ticketResponse = await api.post('/download-tickets', { target: endpoint });
  const ticket = ticketResponse.data?.data?.ticket;
  if (!ticket) {
    throw new Error('无法创建下载授权');
  }

  const downloadEndpoint = withDownloadTicket(endpoint, ticket);
  const link = document.createElement('a');
  link.href = `${API_BASE}${downloadEndpoint}`;
  link.download = fallbackName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  return fallbackName;
};

// 请求拦截器：自动携带 token
api.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const fingerprint = getSafeDeviceFingerprint();
  if (fingerprint) {
    config.headers['x-device-fingerprint'] = fingerprint;
  }
  return config;
});

// 响应拦截器：401 跳转登录（排除登录/注册接口本身）
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/register');
      if (!isAuthEndpoint) {
        const code = error.response?.data?.code;
        localStorage.removeItem('token');
        if (code === 'SESSION_EXPIRED') {
          alert('账号已在其他设备登录，请重新登录');
        }
        if (code === 'DEVICE_REVOKED' || code === 'DEVICE_BLOCKED' || code === 'DEVICE_PENDING') {
          alert(error.response?.data?.message || '当前设备授权已失效，请重新登录后等待管理员重新授权');
        }
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const getDownloadEndpoint = (id) => `/download${buildDownloadQuery({ id })}`;
export const getDailyDownloadEndpoint = (productId) => `/versions/daily-download${buildDownloadQuery({ product_id: productId })}`;
export const getDownloadUrl = (id) => `${API_BASE}${getDownloadEndpoint(id)}`;
export const getDailyDownloadUrl = (productId) => `${API_BASE}${getDailyDownloadEndpoint(productId)}`;
export const downloadVersionFile = (id) => downloadApiFile(getDownloadEndpoint(id));
export const downloadDailyVersionFile = (productId) => downloadApiFile(getDailyDownloadEndpoint(productId));

// 认证相关
export const sendRegistrationCode = (data) => api.post('/auth/register-code', data);
export const sendPasswordResetCode = (data) => api.post('/auth/password-reset-code', data);
export const resetPassword = (data) => api.post('/auth/reset-password', data);
export const register = (data) => api.post('/auth/register', data);
export const login = (data) => api.post('/auth/login', data);
export const changePassword = (data) => api.post('/auth/change-password', data);
export const updateProfile = (data) => api.put('/auth/me', data);
export const logoutApi = () => api.post('/auth/logout');
export const getUsers = () => api.get('/auth/users');
export const getPendingUsers = () => api.get('/auth/pending-users');
export const approveUser = (id) => api.post(`/auth/users/${id}/approve`);
export const rejectUser = (id) => api.post(`/auth/users/${id}/reject`);
export const updateUserRole = (id, data) => api.put(`/auth/users/${id}/role`, data);
export const updateUserSuperAdmin = (id, data) => api.put(`/auth/users/${id}/super-admin`, data);
export const deleteUser = (id) => api.delete(`/auth/users/${id}`);
export const getMyDevices = () => api.get('/auth/my-devices');
export const getDevices = (params) => api.get('/auth/devices', { params });
export const approveDevice = (id) => api.post(`/auth/devices/${id}/approve`);
export const blockDevice = (id) => api.post(`/auth/devices/${id}/block`);
export const deleteDevice = (id) => api.delete(`/auth/devices/${id}`);
export const getAuditLogs = (params) => api.get('/auth/audit-logs', { params });
export const getAuditLogOptions = () => api.get('/auth/audit-logs/options');

// 版本相关
export const getVersions = (params) => api.get('/versions', { params });
export const scanVersions = () => api.post('/versions/scan');
export const getVersionDetail = (id) => api.get(`/versions/${id}`);
export const createVersion = (formData) => api.post('/versions', formData);
export const updateVersion = (id, data) => api.put(`/versions/${id}`, data);
export const deleteVersion = (id, data) => api.post(`/versions/${id}/delete`, data, { timeout: 240000 });
export const addVersionRemark = (id, data) => api.post(`/versions/${id}/remarks`, data);
export const deleteVersionRemark = (id, remarkId) => api.delete(`/versions/${id}/remarks/${remarkId}`);
export const createVersionShareLink = (id, data) => api.post(`/versions/${id}/share-links`, data);
export const shareVersionSummaryToFeishu = (id) => api.post(`/versions/${id}/share-summary`);
export const getDashboardStats = () => api.get('/versions/dashboard/stats');
export const getProducts = () => api.get('/versions/products/list');
export const getProductVersionsSummary = (id) => api.get(`/versions/products/${id}/versions-summary`);
export const getVersionReleaseNote = (id) => api.get(`/versions/${id}/release-note`);
export const getGitLabRelease = (id) => api.get(`/versions/${id}/gitlab-release`);
export const getGitLabDownloadEndpoint = (id, assetIndex = 0) => `/versions/${id}/gitlab-download${buildDownloadQuery({ asset_index: assetIndex })}`;
export const getGitLabDownloadUrl = (id, assetIndex = 0) => `${API_BASE}${getGitLabDownloadEndpoint(id, assetIndex)}`;
export const downloadGitLabVersionFile = (id, assetIndex = 0) => downloadApiFile(getGitLabDownloadEndpoint(id, assetIndex));
export const getWelzekViewerAttachmentDownloadEndpoint = (id) => `/versions/${id}/welzek-viewer-download${buildDownloadQuery()}`;
export const downloadWelzekViewerAttachment = (id, fallbackName) => downloadApiFile(
  getWelzekViewerAttachmentDownloadEndpoint(id),
  fallbackName
);
export const publishVersion = (id, data) => api.post(`/versions/${id}/publish`, data, { timeout: 30000 });
export const getPublishJob = (jobId) => api.get(`/versions/publish-jobs/${jobId}`, { timeout: 180000 });
export const prepublishVersion = (id, data) => api.post(`/versions/${id}/prepublish`, data, { timeout: 300000 });
export const uploadPrepublishedVersion = (id, data) => api.post(`/versions/${id}/upload-prepublish`, data, { timeout: 300000 });

// 组件版本相关
export const getComponentVersionAccess = () => api.get('/component-versions/access');
export const getComponentVersionCatalog = () => api.get('/component-versions/catalog');
export const refreshComponentVersions = () => api.post('/component-versions/refresh', {}, { timeout: 120000 });
export const getComponentBuilds = (params) => api.get('/component-versions/builds', { params, timeout: 120000 });
export const getComponentBuildDetail = (id) => api.get(`/component-versions/builds/${id}`);
export const getComponentBuildDownloadEndpoint = (id) => `/component-versions/builds/${id}/download${buildDownloadQuery()}`;
export const downloadComponentBuild = (id, fallbackName) => downloadApiFile(getComponentBuildDownloadEndpoint(id), fallbackName);

// 波形查找
export const getWaveformStatus = () => api.get('/waveforms/status');
export const getWaveformDirectories = (parent = '') => api.get('/waveforms/directories', { params: { parent } });
export const refreshWaveformIndex = () => api.post('/waveforms/refresh');
export const searchWaveforms = (params) => params.mode === 'advanced'
  ? api.post('/waveforms/search', params)
  : api.get('/waveforms/search', { params });
export const downloadWaveformFile = (id, fallbackName) => downloadApiFile(
  `/waveforms/files/${encodeURIComponent(id)}/download${buildDownloadQuery()}`,
  fallbackName
);
export const createWaveformArchive = (fileIds) => api.post('/waveforms/archives', { file_ids: fileIds });
export const getWaveformArchive = (id) => api.get(`/waveforms/archives/${encodeURIComponent(id)}`);
export const downloadWaveformArchive = (id, fallbackName) => downloadApiFile(
  `/waveforms/archives/${encodeURIComponent(id)}/download${buildDownloadQuery()}`,
  fallbackName
);

// 企业微信员工相关
export const syncStaff = () => api.post('/staff/sync');
export const getStaff = (params) => api.get('/staff', { params });
export const getWhitelistGroups = () => api.get('/staff/whitelist-groups');

export default api;
